const HUNDRED = 100;
const FLOAT_COMPARISON_EPSILON = 0.0001; // From Mac app implementation

/**
 * Clamps the number to the range of [min, max]
 * See also: https://github.com/rwaldron/proposal-math-extensions
 */
function clamp(num: number, min: number, max: number): number {
    return Math.max(min, Math.min(num, max));
}

/**
 * Checks if a number is within a range of two other numbers
 */
function isInRange(num: number, min: number, max: number): boolean {
    return num >= min && num <= max;
}

/**
 * Rounds given number according to provided precision
 */
function round(number: number, precision: number): number {
    const formattedNumber = Number(number).toLocaleString("en-US", {
        useGrouping: false,
        maximumFractionDigits: precision
    });

    return Number(formattedNumber);
}

/**
 * Calculates the percentage based distance
 */
function toPercentage(numerator: number, denominator: number): string {
    return `${toPercentageValue(numerator, denominator)}%`;
}

/**
 * Returns the percentage value
 */
function toPercentageValue(numerator: number, denominator: number): number {
    return numerator * HUNDRED / denominator;
}

/**
 * Returns the percentage value of length/total
 */
function percentageLength(length: number, total: number): string {
    return `${round(length * HUNDRED / total, 1)}%`;
}

/**
 * Returns whether two are epsilon close to each other or not
 */
function isAlmostEqual(
    f1: number | null | undefined, f2: number | null | undefined, epsilon: number = FLOAT_COMPARISON_EPSILON
): boolean {
    return f1 === f2 || Math.abs(f1! - f2!) < epsilon;
}

/**
 * Returns rem size of the given value according to the rootFontSize with "rem" suffix added
 */
function toRemSize(value: number, rootFontSize: number): string {
    return `${round(value / rootFontSize, 3)}rem`; // eslint-disable-line no-magic-numbers
}

/**
 * Returns @param m modulo @param n
 */
function mod(m: number, n: number): number {
    return ((m % n) + n) % n;
}

export {
    clamp,
    isInRange,
    round,
    toPercentage,
    toPercentageValue,
    percentageLength,
    isAlmostEqual,
    toRemSize,
    mod
};
